import { InlineFormatBar } from 'components/InlineFormatBar/InlineFormatBar'
import { useState } from 'react'
import { CommentInlineFormatButton } from 'common/Prose/components/TextInlineFormatBar/components/Buttons/CommentInlineFormatButton/CommentInlineFormatButton'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { TextInlineFormatBarProps } from 'common/Prose/components/TextInlineFormatBar/Types'
import Styles from './RevisionsFormatBar.module.scss'
import cx from 'classnames'
import { RevisionPromptInputContainer as RevisionPromptInput } from 'common/Prose/components/TextInlineFormatBar/components/FormatBars/RevisionPromptInput'
import { useFocusWithin } from '@react-aria/interactions'
import { DocStoreContext, useDocStore } from 'stores/DocStore'
import { observer } from 'mobx-react-lite'
import { QueryClientProvider, useQueryClient } from 'react-query'
import { MoreOptionsDropdown } from 'common/Prose/components/TextInlineFormatBar/components/Buttons/MoreOptionsDropdown/MoreOptionsDropdown'
import { LinkInlineFormatButton } from '../Buttons/LinkInlineFormatButton/LinkInlineFormatButton'
import { BoldInlineFormatButton } from '../Buttons/BoldInlineFormatButton'
import { ItalicInlineFormatButton } from '../Buttons/ItalicInlineFormatButton'
import { InlineTextColorButton } from '../Buttons/InlineTextColorButton/InlineTextColorButton'
import { useAlmanacEditorState } from 'stores/EditorState'
import { TurnTextIntoDocInlineFormatButton } from '../Buttons/TurnTextIntoDocInlineFormatButton'
import { CopyToClipboardInlineFormatButton } from '../Buttons/CopyToClipboardInlineFormatButton/CopyToClipboardInlineFormatButton'

type Props = Omit<TextInlineFormatBarProps, 'docStore' | 'queryClient'>

const RevisionsFormatBar = observer(({ layer, view, runItemKey, article }: Props) => {
  const { isMobile } = useResponsiveDesign()
  const [isRevisionPromptFocused, setIsRevisionPromptFocused] = useState(false)
  const docStore = useDocStore()
  const queryClient = useQueryClient()
  const editorState = useAlmanacEditorState()

  const { focusWithinProps } = useFocusWithin({
    onFocusWithinChange: (isFocusWithin) => setIsRevisionPromptFocused(isFocusWithin),
  })

  const [customPrompt, setCustomPrompt] = useState('')

  return (
    <InlineFormatBar className={cx(Styles.formatBar, { [Styles.promptFocused]: isRevisionPromptFocused })}>
      <div className={Styles.inputContainer} {...focusWithinProps}>
        <RevisionPromptInput
          isActive={isRevisionPromptFocused}
          customPrompt={customPrompt}
          onChange={setCustomPrompt}
        />
      </div>
      <div className={Styles.buttonContainer}>
        <InlineTextColorButton view={view} />
        <BoldInlineFormatButton view={view} />
        <ItalicInlineFormatButton view={view} />
        <LinkInlineFormatButton view={view} runItemKey={runItemKey} article={article} />
        <CommentInlineFormatButton
          size="md"
          hide={isMobile}
          view={view}
          layerId={layer.id}
          queryClient={queryClient}
          docStore={docStore}
          runItemKey={runItemKey}
          overrideSelectionRange={editorState?.selection}
        />
        <CopyToClipboardInlineFormatButton
          initialLabel="Copy"
          copyText={view.state.doc.textBetween(view.state.selection.from, view.state.selection.to, '\n')}
        />
        <TurnTextIntoDocInlineFormatButton view={view} article={article} />
        <MoreOptionsDropdown view={view} article={article} />
      </div>
    </InlineFormatBar>
  )
})

const RevisionsFormatBarContainer = ({ docStore, view, queryClient, ...rest }: TextInlineFormatBarProps) => {
  if (!view.editable || !docStore) return null

  return (
    <QueryClientProvider client={queryClient}>
      <DocStoreContext.Provider value={docStore}>
        <RevisionsFormatBar view={view} {...rest} />
      </DocStoreContext.Provider>
    </QueryClientProvider>
  )
}

export { RevisionsFormatBarContainer as RevisionsFormatBar }
